import React, { useCallback, useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";

import ai from "../../../helpers/axios";
import currencyFormatter from "../../../helpers/currencyFormatter";
import formatSignedAmount from "../../../helpers/format-signed-amount";
import EditProductionCommissionModal from "./commission/edit-production-commission-modal";
import EditNetCommissionModal from "./commission/edit-net-commission-modal";

const Amount = ({ value }) => {
  const { text, className } = formatSignedAmount(value);
  return <span className={className}>{text}</span>;
};

const Row = ({ label, value, note, total, testId }) => (
  <tr className={total ? "tv-commission-total" : undefined} data-testid={testId}>
    <td>
      {total ? <b>{label}</b> : label}
      {note && <span className="tv-commission-note"> {note}</span>}
    </td>
    <td className="text-align-right">{total ? <b><Amount value={value}/></b> : <Amount value={value}/>}</td>
  </tr>
);

const SubHeader = ({ label }) => (
  <tr className="tv-commission-subheader">
    <td colSpan={2}><b>{label}</b></td>
  </tr>
);

const formatPercent = (value) => `${Number(value) || 0}%`;

const SectionTitle = ({ title, onEdit, testId }) => (
  <div className="tv-commission-section-header">
    <h6 className="tv-commission-section-title">{title}</h6>
    {onEdit && (
      <button
        type="button"
        title={`Edit ${title}`}
        className="base-button icon-button evo-blue"
        onClick={onEdit}
        data-testid={testId}
      >
        <span className="material-icons">edit</span>
      </button>
    )}
  </div>
);

const SplitRows = ({ section, preSplitLabel, finalLabel, testIdPrefix }) => (
  <>
    {section.split && (
      <>
        <Row label={preSplitLabel} value={section.preSplit} testId={`${testIdPrefix}-pre-split`}/>
        <Row
          label={`Split ${formatPercent(section.split.percent)} with ${section.split.name || "-"}`}
          value={-section.share}
          testId={`${testIdPrefix}-split`}
        />
      </>
    )}
    <Row label={finalLabel} value={section.final} total testId={`${testIdPrefix}-final`}/>
  </>
);

const CommissionBreakdownBlock = ({ tripId, trip, canEdit, refreshKey }) => {
  const { getAccessTokenSilently } = useAuth0();
  const [breakdown, setBreakdown] = useState(null);
  const [error, setError] = useState(false);
  const [editing, setEditing] = useState(null);

  const fetchData = useCallback(async () => {
    if (!tripId) return;
    try {
      const token = await getAccessTokenSilently();
      const { data } = await ai.auth(token).get(`/api/trips/${tripId}/commission-breakdown`);
      setBreakdown(data || null);
      setError(false);
    } catch (err) {
      console.error("Error loading commission breakdown:", err);
      setError(true);
    }
  }, [tripId, getAccessTokenSilently]);

  useEffect(() => {
    fetchData();
  }, [fetchData, refreshKey]);

  const renderBody = () => {
    if (error && !breakdown) {
      return <div className="tv-empty">Failed to load commission breakdown</div>;
    }

    if (!breakdown) {
      return <div className="tv-empty">Loading...</div>;
    }

    const { income, cogs, taxes, production, net } = breakdown;
    const isCustom = breakdown.clientType === "Custom";
    const rateLabel = formatPercent(breakdown.payoutRate);

    return (
      <>
        <h6 className="tv-commission-section-title">Gross Commission</h6>
        <table className="tv-table tv-commission-table" data-testid="commission-gross-section">
          <tbody>
          <SubHeader label="Income"/>
          <Row label="Client Payments - Flight Income" value={income.flightIncome} testId="commission-flight-income"/>
          <Row label="Client Payments - Incidentals" value={income.incidentals} testId="commission-incidentals-income"/>
          <Row label="Passthrough Commission" value={income.passthrough} testId="commission-passthrough"/>
          <Row label="TOTAL INCOME" value={income.total} total testId="commission-total-income"/>

          <SubHeader label="Cost of Goods"/>
          <Row label="Operator Expense - Taxable" value={cogs.operatorTaxable} testId="commission-operator-taxable"/>
          <Row label="Operator Expense - Non-taxable" value={cogs.operatorNonTaxable} testId="commission-operator-non-taxable"/>
          <Row label="Incidentals" value={cogs.incidentals} testId="commission-incidentals-cogs"/>
          <Row label="Referral Fees" value={cogs.referralFees} testId="commission-referral-fees"/>
          <Row label="TOTAL COST OF GOODS" value={cogs.total} total testId="commission-total-cogs"/>

          <SubHeader label="Taxes"/>
          {taxes.fet.map(({ designation, amount }) => (
            <Row key={`fet-${designation}`} label={`FET - ${designation}`} value={amount} testId="commission-fet"/>
          ))}
          {taxes.segmentFees.map(({ designation, amount }) => (
            <Row
              key={`segment-${designation}`}
              label={`Segment Fee - ${designation}`}
              value={amount}
              testId="commission-segment-fee"
            />
          ))}
          <Row label="TOTAL TAXES & FEES" value={taxes.total} total testId="commission-total-taxes"/>

          <Row label="GROSS COMMISSION" value={breakdown.gross} total testId="commission-gross"/>
          </tbody>
        </table>

        <SectionTitle
          title="Production Commission"
          onEdit={canEdit ? () => setEditing("production") : null}
          testId="commission-production-edit"
        />
        <table className="tv-table tv-commission-table" data-testid="commission-production-section">
          <tbody>
          <Row label="Gross Commission" value={breakdown.gross}/>
          <Row label="Adjustment" value={production.adjustment} note={production.note} testId="commission-production-adjustment"/>
          <SplitRows
            section={production}
            preSplitLabel="Pre-split Prod Commission"
            finalLabel="FINAL PROD COMMISSION"
            testIdPrefix="commission-production"
          />
          </tbody>
        </table>

        <SectionTitle
          title="Net Commission Payable"
          onEdit={canEdit ? () => setEditing("net") : null}
          testId="commission-net-edit"
        />
        <table className="tv-table tv-commission-table" data-testid="commission-net-section">
          <tbody>
          <Row label="Gross Commission" value={breakdown.gross}/>
          <tr data-testid="commission-service-fee">
            <td>
              Service Fee
              {net.serviceFeeOverridden && <span className="tv-commission-note"> (overridden)</span>}
              <div className="tv-commission-note">
                one bracket on {currencyFormatter.format(net.serviceFeeBasis)}, taxable + non-taxable, across all signed OFRs
              </div>
            </td>
            <td className="text-align-right"><Amount value={-net.serviceFee}/></td>
          </tr>
          <Row label="Adjustment" value={net.adjustment} note={net.note} testId="commission-net-adjustment"/>
          <SplitRows
            section={net}
            preSplitLabel="Pre-split Net Commission"
            finalLabel="FINAL NET COMMISSION"
            testIdPrefix="commission-net"
          />
          <tr data-testid="commission-client-type">
            <td>Client Type</td>
            <td className="text-align-right">{breakdown.clientType}</td>
          </tr>
          <tr data-testid="commission-payout-rate">
            <td>Payout Rate</td>
            <td className="text-align-right">
              {isCustom || breakdown.tier == null ? rateLabel : `${rateLabel} (Tier ${breakdown.tier})`}
            </td>
          </tr>
          <Row label={`NET PAYABLE (${rateLabel})`} value={breakdown.netPayable} total testId="commission-net-payable"/>
          </tbody>
        </table>
      </>
    );
  };

  return (
    <div className="tv-block tv-commission-breakdown" data-testid="commission-breakdown-block">
      <div className="tv-block-header">
        <h5 className="tv-block-title">Commission Breakdown</h5>
      </div>
      <div className="tv-block-body">{renderBody()}</div>
      {canEdit && breakdown && (
        <>
          <EditProductionCommissionModal
            isOpen={editing === "production"}
            onClose={() => setEditing(null)}
            trip={{ ...trip, _id: tripId }}
            breakdown={breakdown}
            onSaved={setBreakdown}
          />
          <EditNetCommissionModal
            isOpen={editing === "net"}
            onClose={() => setEditing(null)}
            trip={{ ...trip, _id: tripId }}
            breakdown={breakdown}
            onSaved={setBreakdown}
          />
        </>
      )}
    </div>
  );
};

export default CommissionBreakdownBlock;
